import { useGenerationStatus } from '@hooks/apiHooks/generationsHooks';
import { useEffect, useMemo, useRef, useState } from 'react';

interface UseWebpageParams {
  taskId?: string | null;
}

interface UseWebpageResult {
  webpageUrl: string | null;
  isLoadingWebpage: boolean;
  taskId: string | null;
}

export const useWebpage = ({ taskId }: UseWebpageParams): UseWebpageResult => {
  const { data: generationStatus, isLoading } = useGenerationStatus(
    taskId || null,
    !!taskId,
    false,
    false,
  );

  const prevStatusRef = useRef<string | undefined>(undefined);
  const [reloadTimestamp, setReloadTimestamp] = useState<number | null>(null);

  const baseWebpageUrl = useMemo(() => {
    return generationStatus?.webpageUrl || null;
  }, [generationStatus?.webpageUrl]);

  useEffect(() => {
    const currentStatus = generationStatus?.status;
    if (
      prevStatusRef.current !== 'completed' &&
      currentStatus === 'completed' &&
      baseWebpageUrl
    ) {
      setReloadTimestamp(Date.now());
    }
    prevStatusRef.current = currentStatus;
  }, [generationStatus?.status, baseWebpageUrl]);

  const webpageUrl = useMemo(() => {
    if (!baseWebpageUrl) return null;
    if (reloadTimestamp) {
      const separator = baseWebpageUrl.includes('?') ? '&' : '?';
      return `${baseWebpageUrl}${separator}_t=${reloadTimestamp}`;
    }
    return baseWebpageUrl;
  }, [baseWebpageUrl, reloadTimestamp]);

  const isLoadingWebpage = useMemo(() => {
    if (!taskId) return false;
    if (isLoading) return true;
    if (!generationStatus) return true;
    if (generationStatus.status === 'completed' && baseWebpageUrl) return false;
    if (generationStatus.status === 'failed') return false;
    return (
      generationStatus.status === 'processing' ||
      generationStatus.status === 'pending'
    );
  }, [taskId, isLoading, generationStatus, baseWebpageUrl]);

  return {
    webpageUrl,
    isLoadingWebpage,
    taskId: taskId || null,
  };
};
